import React from 'react';
import {
  XMarkIcon,
  TruckIcon,
  MapPinIcon,
  ClockIcon,
  SignalIcon,
  Battery50Icon,
  NoSymbolIcon,
  MapIcon
} from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import { Vehiculo } from '../../services/vehiculosService';

interface ModalMapaVehiculoProps {
  isOpen: boolean;
  onClose: () => void;
  vehiculo: Vehiculo | null;
}

const ModalMapaVehiculo: React.FC<ModalMapaVehiculoProps> = ({
  isOpen,
  onClose,
  vehiculo,
}) => {
  if (!vehiculo) return null;

  const ubicacion = (vehiculo as any).ubicacion || (vehiculo as any).direccion;
  const lat = (vehiculo as any).latitud;
  const lng = (vehiculo as any).longitud;
  const ultimaActualizacion = (vehiculo as any).fechaActualizacion || (vehiculo as any).fecha_actualizacion;
  const tieneCoordenadas = typeof lat === 'number' && typeof lng === 'number';

  const TarjetaEstado: React.FC<{ icono: any; label: string; valor?: string | null; color?: string }> = ({
    icono: Icono,
    label,
    valor,
    color = 'text-blue-500',
  }) => (
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg bg-gray-50 dark:bg-gray-800 ${color}`}>
        <Icono className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
          {valor ?? '—'}
        </p>
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" title={`Mapa - ${vehiculo.placa}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <MapPinIcon className="h-6 w-6 text-purple-600" />
            Ubicación del Vehículo
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Área de mapa (placeholder elegante con gradiente) */}
        <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 mb-6 bg-gradient-to-br from-slate-100 via-blue-50 to-emerald-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800">
          {/* Grid de calles simulado */}
          <div className="absolute inset-0 opacity-20">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid-map" width="60" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 60 0 L 0 0 0 60" fill="none" stroke="currentColor" strokeWidth="1" className="text-gray-500" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid-map)" />
            </svg>
          </div>

          {/* Calles gruesas */}
          <div className="absolute left-0 right-0 top-[35%] h-2 bg-white dark:bg-gray-600 opacity-40 shadow" />
          <div className="absolute left-[55%] top-0 bottom-0 w-2 bg-white dark:bg-gray-600 opacity-40 shadow" />
          <div className="absolute left-0 right-0 top-[70%] h-1.5 bg-white dark:bg-gray-600 opacity-30" />

          {/* Marcador del vehículo */}
          <div className="absolute left-[55%] top-[35%] transform -translate-x-1/2 -translate-y-full">
            <div className="relative flex flex-col items-center">
              <div className="pulse-ping absolute -top-1 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-purple-500/30 animate-ping" />
              <div className="relative z-10 bg-purple-600 text-white p-2.5 rounded-full shadow-xl border-4 border-white dark:border-gray-800">
                <TruckIcon className="h-6 w-6" />
              </div>
              <div className="mt-1 bg-white dark:bg-gray-800 px-2.5 py-1 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-900 dark:text-white whitespace-nowrap">
                {vehiculo.placa}
              </div>
            </div>
          </div>

          {/* Botón de pantalla completa (decorativo) */}
          <div className="absolute top-4 right-4 flex flex-col gap-2">
            <button className="w-9 h-9 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <MapIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Overlay sin datos */}
          {!tieneCoordenadas && !ubicacion && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-gray-900/70 backdrop-blur-sm">
              <div className="text-center max-w-md px-6">
                <div className="inline-flex p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                  <NoSymbolIcon className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  Sin datos de GPS
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Este vehículo no tiene coordenadas ni dirección registrada en este momento.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Tarjetas info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <TarjetaEstado
            icono={MapPinIcon}
            label="Dirección"
            valor={ubicacion || 'No registrada'}
            color="text-purple-500"
          />
          <TarjetaEstado
            icono={SignalIcon}
            label="Coordenadas"
            valor={tieneCoordenadas ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : undefined}
            color="text-green-500"
          />
          <TarjetaEstado
            icono={Battery50Icon}
            label="Señal GPS"
            valor={tieneCoordenadas ? 'Excelente' : 'Sin señal'}
            color={tieneCoordenadas ? 'text-green-500' : 'text-gray-400'}
          />
          <TarjetaEstado
            icono={ClockIcon}
            label="Última transmisión"
            valor={ultimaActualizacion ? new Date(ultimaActualizacion).toLocaleString() : undefined}
            color="text-blue-500"
          />
        </div>

        {/* Acciones */}
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalMapaVehiculo;
